import { InertiaLink, usePage } from "@inertiajs/inertia-react";
import React, { useState, useRef, useEffect } from "react";
import AppLayout from "@/Layouts/AppLayout";
import AdminLayout from "@/Layouts/AdminLayout";
import { useTitle } from "@/Utilities/Hooks";
import { generateDefaultDatespan } from "@/Utilities/Utilities";

import {
  Subheader,
  BlockButton,
  NumberLinkBlock,
  GidasFeed,
  ContentSlider,
  Video,
  Emma,
} from "@/Components/";
import { ErrorMessage, SuccessMessage } from "@/Components/Messages";
import { LoadingProvider } from "@/Contexts/LoadingContext";
import { Budget } from "@/Pages/Admin/Budget";
import OrganizationLayout from "@/Layouts/OrganizationLayout";
import LayoutIcon from "@/Components/LayoutIcon";
import { House } from "@/Icons";

import { useSetAtom } from "jotai";
import {
  teamFilterAtom,
  questionFilterAtom,
  dateFilterAtom,
  locationFilterAtom,
  generalFilterAtom,
} from "@/Stores";

export default function General({
  auth,
  flash,
  errors,
  active_calculators,
  active_teams,
  active_questions,
}) {
  useTitle("Welcome to Emma");

  // Oh god forgive me for what I'm about to do
  const orgId = usePage()?.props?.organization?.data?.id;
  const storageBucket =
    parseInt(localStorage.getItem("dashboardStorageBucket")) || 0;
  const setTeamFilter = useSetAtom(teamFilterAtom);
  const setQuestionFilter = useSetAtom(questionFilterAtom);
  const setDateFilter = useSetAtom(dateFilterAtom);
  const setLocationFilter = useSetAtom(locationFilterAtom);
  const setGeneralFilter = useSetAtom(generalFilterAtom);

  useEffect(() => {
    // reset all filters
    if (orgId !== storageBucket) {
      setTeamFilter(null);
      setQuestionFilter(null);
      setDateFilter(generateDefaultDatespan());
      setLocationFilter(null);
      setGeneralFilter({ source: [], format: ["ar5"], reports: "all" });
      localStorage.setItem("dashboardStorageBucket", orgId);
    }
  }, [orgId]);

  let [modal, setModal] = useState({
    open: false,
    title: "",
    content: "",
    class: "",
  });
  let [subscribed, setSubscribed] = useState(false); // TODO: get inital status if user is subscribed or not
  const isInitialMount = useRef(true);

  const isReturningUser = () => {
    return active_teams > 0 || active_calculators > 0; // NOTE: Is this good enough? Don't check questions, can they be active without a team with modules?
  };

  function openModal(t, d, c) {
    setModal({ open: true, title: t, content: d, class: c });
  }

  function closeModal() {
    setModal({ open: false, title: "", content: "", class: "" });
  }

  const Workflow = () => {
    return (
      <>
        <Subheader className="mt-8">EMISSION ACCOUNTING WORKFLOW</Subheader>
        <div className="relative mb-4 flex w-full flex-col items-center justify-center">
          <div className="relative flex w-full flex-col overflow-visible pt-4 sm:flex-row shadow-small bg-gray-light">
            <ContentSlider
              elements={3}
              elementsInView={1}
              nextButtons={["Fill in the numbers", "Get your report", ""]}
            >
              <div className="flex w-full flex-shrink-0 flex-grow flex-col flex-nowrap px-8 py-4 md:flex-row">
                <div className="w-full md:w-2/3">
                  <Subheader margin="mb-4" className="text-typo">
                    TAKE ACTIONS TO REDUCE CO₂
                  </Subheader>
                  <h3 className="mb-4 text-xl font-bold">Set up the tool</h3>
                  <p>
                    Add multiple departments and reporters to simplify reporting
                    across the organisation. Add questions and choose emission
                    factors from our global database or create your own. Add
                    reporters to tasks and customize reporting categories based
                    on you goal and scope.
                  </p>
                  <p className="mt-6">
                    <InertiaLink
                      href={route("company.settings")}
                      className="btn-outline mr-2 border-primary p-2"
                    >
                      Go to settings
                    </InertiaLink>
                    <InertiaLink href={route("teams")} className="btn-blue">
                      Go to Teams
                    </InertiaLink>
                  </p>
                </div>
                <div className="mt-4 w-full md:mt-0 md:w-1/3">
                  <img
                    className="mb:mb-0 relative z-[100] m-auto mb-2"
                    src={"images/workflow_1.png"}
                    alt="Workflow 1"
                  />
                </div>
              </div>
              <div className="flex w-full flex-shrink-0 flex-grow flex-col flex-nowrap px-8 py-4 md:flex-row">
                <div className="w-full md:w-2/3">
                  <Subheader margin="mb-4" className="text-typo">
                    REQUEST ACTIVITY DATA
                  </Subheader>
                  <h3 className="mb-4 text-xl font-bold">
                    Fill in the numbers
                  </h3>
                  <p>
                    Start filling in the numbers, keep track of your reporters
                    and follow up on progress. Emma calculates emission
                    performance automatically. Add new tasks and teams whenever
                    and request data from suppliers if necessary.
                  </p>
                  <p className="mt-6">
                    {/* <InertiaLink href="#tutorials" className="p-2 mr-2 border-2 btn-outline border-blue">Watch tutorial</InertiaLink> */}
                    <InertiaLink href={route("teams")} className="btn-blue">
                      Go to Reporting
                    </InertiaLink>
                  </p>
                </div>
                <div className="mt-4 w-full md:mt-0 md:w-1/3">
                  <img
                    className="mb:mb-0 relative z-[100] m-auto mb-2"
                    src={"images/workflow_2.png"}
                    alt="Workflow 2"
                  />
                </div>
              </div>
              <div className="flex w-full flex-shrink-0 flex-grow flex-col flex-nowrap px-8 py-4 md:flex-row">
                <div className="w-full md:w-2/3">
                  <Subheader margin="mb-4" className="text-typo">
                    ANALYSE YOUR ENVIRONMENTAL PERFORMANCE
                  </Subheader>
                  <h3 className="mb-4 text-xl font-bold">Get your report</h3>
                  <p>
                    Your statistics are always available via Results. Analyse
                    your environmental performance and understand you emission
                    hot-spots. Start implement actions to reduce CO₂ emissions.
                  </p>
                  <p className="mt-6">
                    {/* <InertiaLink href="#tutorials" className="p-2 mr-2 border-2 btn-outline border-blue">Watch tutorial</InertiaLink> */}
                    <InertiaLink href={route("dashboard")} className="btn-blue">
                      Go to Results
                    </InertiaLink>
                  </p>
                </div>
                <div className="mt-4 w-full md:mt-0 md:w-1/3">
                  <img
                    className="mb:mb-0 relative z-[100] m-auto mb-2"
                    src={"images/workflow_3.png"}
                    alt="Workflow 3"
                  />
                </div>
              </div>
            </ContentSlider>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="flex flex-col items-stretch">
      {flash.status && (
        <SuccessMessage className="mb-8">{flash.status}</SuccessMessage>
      )}

      {errors?.message && (
        <ErrorMessage className="mb-8">{errors?.message}</ErrorMessage>
      )}

      {/* <div className="my-4 flex flex-shrink-0 flex-row">
        <div className="w-full sm:w-2/3">
          <h1 className="header-1 mb-4">Welcome to Emma, {user.firstname}</h1>
          <p className="text-gray-700">
            Save time, get a daily overview of your emissions and improve
            accuracy with the Emma workflow.
          </p>
        </div>
      </div> */}

      {!isReturningUser() && (
        <>
          <div className="mt-4 flex flex-shrink-0 flex-row justify-between rounded-sm bg-primary-light p-8">
            <div className="hidden flex-shrink justify-start pr-4 md:flex">
              <Emma className="self-end" color="green" />
            </div>
            <div className="w-full flex-grow pl-4">
              <h2 className="header-2 mb-4">Welcome to Emma</h2>
              <p className="text-gray-700">
                Emma is a streamlined calculation tool that helps you account
                green house gas emissions to air from your daily operations.
                Save time, get a daily overview of your emissions and improve
                accuracy with the Emma workflow.{" "}
              </p>
            </div>
          </div>
          <Workflow />
        </>
      )}

      <div className="my-4 flex flex-shrink-0 flex-row flex-wrap gap-4 md:flex-nowrap">
        <BlockButton
          title="REPORTING"
          text="Report data"
          href={route("teams")}
          bgcolor="bg-primary hover:bg-primary-dark"
        />
        <BlockButton
          title="TEAMS"
          text="Manage your teams"
          href={route("teams")}
          bgcolor="bg-primary hover:bg-primary-dark"
          // bgcolor="bg-purple hover:bg-purple-800"
        />
        <BlockButton
          title="RESULTS"
          text="Go to results"
          href={route("dashboard")}
          bgcolor="bg-primary hover:bg-primary-dark"
          // bgcolor="bg-blue-dark hover:bg-[#1E293B]"
        />
      </div>

      <LoadingProvider>
        <Budget />
      </LoadingProvider>

      <Subheader className="mt-10">WHAT WOULD YOU LIKE TO DO</Subheader>
      <div className="flex flex-col gap-4 md:flex-row">
        <NumberLinkBlock
          title="ACTIVE CALCULATORS"
          number={active_calculators}
        />
        <NumberLinkBlock
          title="ACTIVE TEAMS"
          number={active_teams}
          link="Go to teams overview"
          href={route("teams")}
        />
        <NumberLinkBlock title="ACTIVE QUESTIONS" number={active_questions} />
      </div>

      {isReturningUser() && <Workflow />}

      {/* <Subheader id="tutorials" className="mt-10">TUTORIALS</Subheader>
      <div className="relative flex flex-col items-center justify-center w-full">
        <div className="relative flex flex-col w-full overflow-visible bg-white sm:flex-row">
          <ContentSlider elements={6} elementsInView={3} className="px-2 pt-4" showNext={"More tutorials"}>
            <div className="flex-shrink-0 w-full px-2 pb-6 md:w-1/3">
              <Video video="introduction_to_emma"><p className="mt-3">Set up your organization 1</p></Video>
            </div>
            <div className="flex-shrink-0 w-full px-2 pb-6 md:w-1/3">
              <Video video="sign_up"><p className="mt-3">Start an accounting period 2</p></Video>
            </div>
            <div className="flex-shrink-0 w-full px-2 pb-6 md:w-1/3">
              <Video video="introduction_to_emma"><p className="mt-3">Start an accounting period 3</p></Video>
            </div>
            <div className="flex-shrink-0 w-full px-2 pb-6 md:w-1/3">
              <Video video="sign_up"><p className="mt-3">Set up your organization 4</p></Video>
            </div>
            <div className="flex-shrink-0 w-full px-2 pb-6 md:w-1/3">
              <Video video="introduction_to_emma"><p className="mt-3">Start an accounting period 5</p></Video>
            </div>
            <div className="flex-shrink-0 w-full px-2 pb-6 md:w-1/3">
              <Video video="sign_up"><p className="mt-3">Start an accounting period 6</p></Video>
            </div>
          </ContentSlider>
        </div>
      </div> */}

      <Subheader className="mt-10">NEWS</Subheader>
      <LoadingProvider>
        <GidasFeed count="3" />
      </LoadingProvider>
    </div>
  );
}

General.layout = (page) => {
  const user = page?.props?.auth?.user?.data;
  return (
    <AppLayout>
      <OrganizationLayout
        header={`Welcome to Emma, ${user.firstname}`}
        icon={
          <LayoutIcon>
            <House size={40} className="text-black" />
          </LayoutIcon>
        }
        byline="Save time, get a daily overview of your emissions and improve accuracy with the Emma workflow."
      >
        {page}
      </OrganizationLayout>
    </AppLayout>
  );
};
